import { isRestrictedTerritory } from '../config/restrictedTerritories';

interface LocationResponse {
  countryCode: string | null;
  countryName: string | null;
  city: string | null;
  ip: string | null;
  error?: string;
}

const DEFAULT_RESPONSE: LocationResponse = {
  countryCode: null,
  countryName: null,
  city: null,
  ip: null,
};

const LOCATION_CACHE_KEY = 'olympex_location_data';
const LOCATION_CACHE_TIMESTAMP_KEY = 'olympex_location_timestamp';
const CACHE_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

const cacheLocationData = (locationData: LocationResponse) => {
  try {
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(locationData));
    localStorage.setItem(LOCATION_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to cache location data:', error);
  }
};

const getCachedLocationData = (): LocationResponse | null => {
  try {
    const cachedData = localStorage.getItem(LOCATION_CACHE_KEY);
    const timestamp = localStorage.getItem(LOCATION_CACHE_TIMESTAMP_KEY);
    
    if (!cachedData || !timestamp) {
      return null;
    }
    
    const timestampValue = parseInt(timestamp, 10);
    
    if (Date.now() - timestampValue > CACHE_EXPIRATION_TIME) {
      localStorage.removeItem(LOCATION_CACHE_KEY);
      localStorage.removeItem(LOCATION_CACHE_TIMESTAMP_KEY);
      return null;
    }
    
    return JSON.parse(cachedData);
  } catch (error) {
    console.warn('Failed to retrieve cached location data:', error);
    return null;
  }
};

export const getUserLocation = async (): Promise<LocationResponse> => {
  const cachedLocation = getCachedLocationData();
  if (cachedLocation) {
    return cachedLocation;
  }
  
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) {
      throw new Error('Unable to fetch location data');
    }
    
    const data = await response.json();
    
    const locationData = {
      countryCode: data.country_code || null,
      countryName: data.country_name || null,
      city: data.city || null,
      ip: data.ip || null,
    };
    
    cacheLocationData(locationData);
    
    return locationData;
  } catch (error) {
    console.error('Error fetching location:', error);
    return {
      ...DEFAULT_RESPONSE,
      error: error instanceof Error ? error.message : 'Unknown error getting location',
    };
  }
};

export const checkLocationRestriction = async (): Promise<{
  isRestricted: boolean;
  location: LocationResponse;
}> => {
  const location = await getUserLocation();
  
  return {
    isRestricted: isRestrictedTerritory(location.countryCode),
    location,
  };
}; 